import { useMemo, useState, useEffect } from 'react';
import {
  List,
  Utils,
  ListCard,
  isMobile,
  FlexLayout,
} from '@gv/triage-components';

import { api } from 'api';
import { useAppSelector } from 'store';
import { MatrixConfigKey } from 'types';
import { useHospitalParams } from 'hooks';
import { useGetHospitalQuery } from 'store/api/hospital-bitwerx';
import { selectCurrentTask } from 'store/api/action-queue/selectors';

import * as Styles from './styles';
import { SchedulingMatrixTableProps } from './types';
import { weekDays, getTableProps, getDurationTitle } from './config';

const {
  Object: { getValueByPath },
  Helpers: { textValue, formatPrice },
} = Utils;

const mobileTemplate = 'repeat(2, 1fr)';
import { useListQuery as useProviderListQuery } from 'store/api/providers-bitwerx';

export const SchedulingMatrixTable = ({
  widgetView,
  matrices = [],
  matrixConfig = {},
  ...props
}: SchedulingMatrixTableProps) => {
  const mobile = isMobile();
  const { id: hospitalId } = useHospitalParams();
  const task = useAppSelector(selectCurrentTask);
  let { data: dataProviders } = useProviderListQuery({
    id: hospitalId || task?.h_id,
  });
  const { data: hospitalBitwerx } = useGetHospitalQuery({
    id: hospitalId || task?.h_id,
  }) as {
    data?: {
      response?: {
        bitwerx_pims?: string;
        pims_location_id?: string;
        bitwerx_practice_id?: string;
      };
    };
  };
  let filterArray = [
    'price',
    'doctor',
    'appointment',
    'examRoom',
    'colorCode',
    'timeBlock',
    'dayOfWork',
    'appointmentLength',
    'specialInstructions',
  ];
  const isBitwerx =
    hospitalBitwerx?.response?.bitwerx_pims ||
    hospitalBitwerx?.response?.pims_location_id;
  if (isBitwerx) {
    filterArray = [
      'provider',
      'appointment',
      'examRoom',
      'appointmentLength',
      'dayOfWork',
      'colorCode',
      'timeBlock',
      'specialInstructions',
      'price',
    ];
  }
  matrixConfig = Object.fromEntries(
    Object.entries(matrixConfig).filter(([key]) => filterArray.includes(key))
  );
  const { titles, template, filtered } = useMemo(
    () =>
      getTableProps({
        matrixConfig,
        isBitwerx: Boolean(
          hospitalBitwerx?.response?.bitwerx_pims ||
            hospitalBitwerx?.response?.pims_location_id
        ),
      }),
    [matrixConfig, hospitalBitwerx?.response]
  );

  const mainWrapperStyles = useMemo(
    () =>
      widgetView
        ? { padding: '8px 0', minWidth: mobile ? 0 : '900px' }
        : undefined,
    [mobile, widgetView]
  );

  if (widgetView && !matrices?.length) {
    return null;
  }

  // Fetch rooms using direct API call
  const [dataRooms, setDataRooms] = useState<any[]>([]);

  useEffect(() => {
    const fetchRooms = async () => {
      const targetHospitalId = hospitalId || task?.h_id;
      if (!targetHospitalId) {
        setDataRooms([]);
        return;
      }

      try {
        const response = await api.masterBitwerx.getRooms(
          Number(targetHospitalId)
        );
        const roomsData =
          response?.data?.data?.rooms || response?.data?.rooms || [];
        setDataRooms(roomsData);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setDataRooms([]);
      }
    };

    fetchRooms();
  }, [hospitalId, task?.h_id]);
  const table = (
    <List
      horizontalScroll
      count={matrices?.length ?? 0}
      mainWrapperStyles={mainWrapperStyles}
      header={
        matrices?.length ? (
          <Styles.ListHeader
            titles={titles}
            padding="8px 16px"
            template={template}
            tabletTitles={titles}
            widgetView={widgetView}
            tabletTemplate={template}
          />
        ) : undefined
      }
      {...props}
    >
      {matrices.map((matrix, idx) => (
        <ListCard
          key={idx}
          template={template}
          padding="12px 16px"
          mobileHeaders={titles}
          mobileTemplate={mobileTemplate}
          verticalAlign={mobile ? 'flex-start' : undefined}
          values={filtered.map(({ key, bold, valuePath, timeTitles }) => {
            let value = getValueByPath(matrix, valuePath);
            if (
              hospitalBitwerx?.response?.bitwerx_pims &&
              key === MatrixConfigKey.ExamRoom
            ) {
              value = dataRooms?.find(
                (room) => room.other_source_id === value
              )?.displayName;
            }
            if (key === MatrixConfigKey.Provider) {
              {
                return value.map((data: any) => (
                  <Styles.Ellipsis key={data.provider_id}>
                    {textValue(
                      key === MatrixConfigKey.Provider
                        ? dataProviders?.find(
                            (obj) => data.provider_id === obj.other_source_id
                          )?.displayName
                        : data?.dayOfWorks
                            ?.map((item: number) => weekDays[item])
                            .join(', ')
                    )}
                  </Styles.Ellipsis>
                ));
              }
            } else if (Array.isArray(value)) {
              return (
                <FlexLayout fullWidth minWidth={0} flexDirection="column">
                  {value.map((item, itemIdx) => {
                    const isDoctor = key === MatrixConfigKey.Doctor;
                    const isDaysOfWork = key === MatrixConfigKey.DaysOfWork;

                    const doctorName = item?.name;
                    const doctorDays =
                      item?.SchedulingMatrixDoctors?.dayOfWorks ?? [];
                    const providerDays = item?.dayOfWorks ?? [];

                    let displayText: any = '';

                    if (isDoctor) {
                      displayText = textValue(doctorName);
                    } else if (isDaysOfWork) {
                      const days = doctorDays.length
                        ? doctorDays
                        : providerDays;
                      displayText = days.length
                        ? days.map((d: number) => weekDays[d]).join(', ')
                        : '-';
                    }

                    return (
                      <Styles.Ellipsis key={itemIdx}>
                        {displayText}
                      </Styles.Ellipsis>
                    );
                  })}
                </FlexLayout>
              );
            } else {
              const tableValue = textValue(value);
              return key === MatrixConfigKey.SpecialInstructions ? (
                <p>{tableValue}</p>
              ) : (
                <Styles.Ellipsis bold={bold}>
                  {value && key === MatrixConfigKey.Price
                    ? formatPrice(value, '$0,0[.]00')
                    : timeTitles
                      ? getDurationTitle(value)
                      : tableValue}
                </Styles.Ellipsis>
              );
            }
          })}
        />
      ))}
    </List>
  );

  return !widgetView ? (
    table
  ) : (
    <FlexLayout gap={8} flexDirection="column">
      <p className="semibold">Please review the hospital scheduling matrix</p>
      <Styles.Container>{table}</Styles.Container>
    </FlexLayout>
  );
};
